/**
 * Lackey — dev-only remote eval bridge (browser side). Pairs with `tools/lackey/` (Node server + CLI).
 *
 * Wire once at startup, register channels anywhere:
 *
 *   import lackey from './dev/lackey';
 *   lackey.connect({ port: import.meta.env.VITE_LACKEY_PORT }); // inert when unset / 0
 *   lackey.channel('dom', () => window);
 *
 * Code sent from the CLI runs as an async function body with `this` bound to the channel's
 * context and one argument, `lackey`, exposing `sleep` and `waitFor`. The return value is
 * serialized (bounded depth/size) and sent back as JSON.
 */

/**
 * @typedef {unknown | (() => unknown)} ChannelContext
 * @typedef {{ port?: number | string | null, host?: string, url?: string, secure?: boolean, reconnect?: boolean }} ConnectOptions
 * @typedef {{ timeout?: number, interval?: number, label?: string }} WaitForOptions
 */

const MAX_DEPTH = 6;
const MAX_ITEMS = 100;
const RECONNECT_MIN_MS = 1000;
const RECONNECT_MAX_MS = 15000;

/** @type {Map<string, ChannelContext>} */
const contexts = new Map();
/** @type {WebSocket | null} */
let socket = null;
/** @type {string | null} */
let connectUrl = null;
let active = false;
let reconnectEnabled = true;
let reconnectDelay = RECONNECT_MIN_MS;
/** @type {ReturnType<typeof setTimeout> | null} */
let reconnectTimer = null;

// ---------------------------------------------------------------------------
// helpers exposed to evals

const helpers = {
  /** @param {number} ms */
  sleep: (ms) => new Promise((resolve) => setTimeout(resolve, ms)),
  /**
   * Poll `probe` until it returns a truthy value.
   * @template T
   * @param {() => T | Promise<T>} probe
   * @param {WaitForOptions} [opts]
   * @returns {Promise<T>}
   */
  async waitFor(probe, opts = {}) {
    const timeout = opts.timeout ?? 5000;
    const interval = opts.interval ?? 50;
    const start = Date.now();
    let lastError;
    while (Date.now() - start < timeout) {
      try {
        const value = await probe();
        if (value) {
          return value;
        }
      } catch (e) {
        lastError = e;
      }
      await helpers.sleep(interval);
    }
    const label = opts.label ? `: ${opts.label}` : '';
    const last = lastError ? ` (last error: ${errorMessage(lastError)})` : '';
    throw new Error(`waitFor timed out after ${timeout}ms${label}${last}`);
  },
};

// ---------------------------------------------------------------------------
// serialization

/** @param {unknown} e */
function errorMessage(e) {
  return e instanceof Error ? e.message : String(e);
}

/** @param {Node} node */
function describeNode(node) {
  if (node.nodeType === Node.TEXT_NODE) {
    return `#text ${JSON.stringify((node.textContent || '').trim().slice(0, 60))}`;
  }
  if (node.nodeType === Node.DOCUMENT_NODE) {
    return '#document';
  }
  const el = /** @type {Element} */ (node);
  const tag = (el.tagName || node.nodeName || '').toLowerCase();
  const id = el.id ? `#${el.id}` : '';
  const classes = el.classList && el.classList.length ? `.${Array.from(el.classList).slice(0, 4).join('.')}` : '';
  return `<${tag}${id}${classes}>`;
}

/** @param {object} value */
function constructorName(value) {
  const ctor = /** @type {{ constructor?: { name?: string } }} */ (value).constructor;
  return (ctor && ctor.name) || 'Object';
}

/**
 * @param {unknown} value
 * @param {number} depth
 * @param {Set<object>} seen
 * @returns {unknown}
 */
function toWire(value, depth, seen) {
  if (value === undefined || value === null) {
    return null;
  }
  const t = typeof value;
  if (t === 'string' || t === 'boolean') {
    return value;
  }
  if (t === 'number') {
    return Number.isFinite(value) ? value : String(value);
  }
  if (t === 'bigint' || t === 'symbol') {
    return String(value);
  }
  if (t === 'function') {
    return `[Function ${/** @type {Function} */ (value).name || 'anonymous'}]`;
  }
  if (value instanceof Date) {
    return Number.isNaN(value.getTime()) ? 'Invalid Date' : value.toISOString();
  }
  if (value instanceof Error) {
    return { name: value.name, message: value.message };
  }
  if (typeof Node !== 'undefined' && value instanceof Node) {
    return describeNode(value);
  }
  if (typeof Window !== 'undefined' && value instanceof Window) {
    return '[Window]';
  }

  const obj = /** @type {object} */ (value);
  if (depth >= MAX_DEPTH) {
    return Array.isArray(obj) ? `[Array(${obj.length})]` : `[${constructorName(obj)}]`;
  }
  if (seen.has(obj)) {
    return '[Circular]';
  }
  seen.add(obj);
  try {
    const isNodeList = typeof NodeList !== 'undefined' && obj instanceof NodeList;
    const isCollection = typeof HTMLCollection !== 'undefined' && obj instanceof HTMLCollection;
    if (Array.isArray(obj) || obj instanceof Set || isNodeList || isCollection) {
      const arr = Array.from(/** @type {Iterable<unknown>} */ (obj));
      /** @type {unknown[]} */
      const out = arr.slice(0, MAX_ITEMS).map((item) => toWire(item, depth + 1, seen));
      if (arr.length > MAX_ITEMS) {
        out.push(`… ${arr.length - MAX_ITEMS} more`);
      }
      return out;
    }
    if (obj instanceof Map) {
      /** @type {Record<string, unknown>} */
      const out = {};
      let n = 0;
      for (const [k, v] of obj) {
        if (n++ >= MAX_ITEMS) {
          out['…'] = `${obj.size - MAX_ITEMS} more entries`;
          break;
        }
        out[String(k)] = toWire(v, depth + 1, seen);
      }
      return out;
    }
    const withToJSON = /** @type {{ toJSON?: () => unknown }} */ (obj);
    if (typeof withToJSON.toJSON === 'function') {
      try {
        return toWire(withToJSON.toJSON(), depth + 1, seen);
      } catch {
        // fall through to key enumeration
      }
    }
    const keys = Object.keys(obj);
    /** @type {Record<string, unknown>} */
    const out = {};
    for (let i = 0; i < keys.length; i++) {
      if (i >= MAX_ITEMS) {
        out['…'] = `${keys.length - MAX_ITEMS} more keys`;
        break;
      }
      const key = keys[i];
      try {
        out[key] = toWire(/** @type {Record<string, unknown>} */ (obj)[key], depth + 1, seen);
      } catch (e) {
        out[key] = `[Unreadable: ${errorMessage(e)}]`;
      }
    }
    return out;
  } finally {
    seen.delete(obj);
  }
}

/** @param {unknown} value */
export function serialize(value) {
  try {
    return JSON.parse(JSON.stringify(toWire(value, 0, new Set())));
  } catch (e) {
    return { __lackeyError: `not serializable: ${errorMessage(e)}`, string: String(value) };
  }
}

// ---------------------------------------------------------------------------
// eval

/** @param {string} channel */
function resolveContext(channel) {
  const entry = contexts.get(channel);
  if (entry === undefined) {
    return undefined;
  }
  return typeof entry === 'function' ? entry() : entry;
}

/**
 * @param {string} code
 * @param {unknown} ctx
 * @returns {Promise<{ ok: true, result: unknown } | { ok: false, error: string }>}
 */
async function runEval(code, ctx) {
  let fn;
  try {
    // eslint-disable-next-line no-new-func
    fn = new Function('lackey', `return (async function () {\n${code}\n}).call(this, lackey);`);
  } catch (e) {
    return { ok: false, error: `SyntaxError: ${errorMessage(e)}` };
  }
  try {
    const raw = await fn.call(ctx, helpers);
    return { ok: true, result: serialize(raw) };
  } catch (e) {
    return { ok: false, error: e instanceof Error ? `${e.name}: ${e.message}` : String(e) };
  }
}

// ---------------------------------------------------------------------------
// transport

/** @param {object} payload */
function send(payload) {
  if (socket && socket.readyState === WebSocket.OPEN) {
    socket.send(JSON.stringify(payload));
  }
}

/** @param {MessageEvent} ev */
async function onMessage(ev) {
  let msg;
  try {
    msg = JSON.parse(String(ev.data));
  } catch {
    return;
  }
  if (msg.role !== 'server' || msg.type !== 'eval') {
    return;
  }
  const channel = String(msg.channel || '');
  const id = String(msg.id || '');
  const base = { role: 'browser', type: 'eval_result', channel, id };

  let ctx;
  try {
    ctx = resolveContext(channel);
  } catch (e) {
    send({ ...base, ok: false, error: `context getter threw: ${errorMessage(e)}` });
    return;
  }
  if (ctx === undefined) {
    send({
      ...base,
      ok: false,
      error: contexts.has(channel)
        ? `channel "${channel}" getter returned undefined (component unmounted?)`
        : `no context registered for channel "${channel}"`,
    });
    return;
  }
  const out = await runEval(String(msg.code || ''), ctx);
  send({ ...base, ...out });
}

/** @param {unknown} raw */
function parsePort(raw) {
  if (raw === undefined || raw === null) {
    return null;
  }
  const s = String(raw).trim();
  if (!s) {
    return null;
  }
  const n = Number.parseInt(s, 10);
  return Number.isInteger(n) && n > 0 ? n : null;
}

/**
 * HTTPS pages cannot open ws:// (mixed content). Default to wss://localhost
 * so a typical mkcert localhost.pem matches. HTTP pages stay on ws://127.0.0.1.
 * @param {ConnectOptions} opts
 * @param {number | null} port
 */
function resolveConnectUrl(opts, port) {
  if (port === null) {
    return null;
  }
  const pageSecure = typeof location !== 'undefined' && location.protocol === 'https:';
  const secure = opts.secure ?? pageSecure;
  const host = opts.host || (secure ? 'localhost' : '127.0.0.1');
  return `${secure ? 'wss' : 'ws'}://${host}:${port}`;
}

function scheduleReconnect() {
  if (!reconnectEnabled || reconnectTimer || !active) {
    return;
  }
  reconnectTimer = setTimeout(() => {
    reconnectTimer = null;
    reconnectDelay = Math.min(reconnectDelay * 2, RECONNECT_MAX_MS);
    open();
  }, reconnectDelay);
}

function open() {
  if (!connectUrl || !active) {
    return;
  }
  if (socket && (socket.readyState === WebSocket.OPEN || socket.readyState === WebSocket.CONNECTING)) {
    return;
  }
  const ws = new WebSocket(connectUrl);
  socket = ws;
  ws.addEventListener('open', () => {
    reconnectDelay = RECONNECT_MIN_MS;
    contexts.forEach((_ctx, channel) => send({ role: 'browser', type: 'register', channel }));
  });
  ws.addEventListener('message', (ev) => {
    void onMessage(ev);
  });
  ws.addEventListener('close', () => {
    if (socket === ws) {
      socket = null;
    }
    scheduleReconnect();
  });
}

// ---------------------------------------------------------------------------
// public API

const lackey = {
  /** True once `connect()` was given a usable port or url. */
  get enabled() {
    return active;
  },
  /** True while the socket to the Lackey server is open. */
  get connected() {
    return !!socket && socket.readyState === WebSocket.OPEN;
  },

  /**
   * Connect to the Lackey server. Returns false (and stays inert) when no valid port/url is given.
   * @param {ConnectOptions} [opts]
   */
  connect(opts = {}) {
    const port = parsePort(opts.port);
    const url = opts.url ?? resolveConnectUrl(opts, port);
    if (!url || typeof WebSocket === 'undefined') {
      active = false;
      return false;
    }
    lackey.disconnect();
    active = true;
    connectUrl = url;
    reconnectEnabled = opts.reconnect !== false;
    reconnectDelay = RECONNECT_MIN_MS;
    /** @type {any} */ (globalThis).__lackey = lackey;
    open();
    return true;
  },

  disconnect() {
    active = false;
    if (reconnectTimer) {
      clearTimeout(reconnectTimer);
      reconnectTimer = null;
    }
    if (socket) {
      const ws = socket;
      socket = null;
      ws.close();
    }
  },

  /**
   * Bind a channel name to the `this` evals run against. Pass a getter (`() => this`) so the
   * context stays current. Safe to call before `connect()` and when Lackey is off.
   * @param {string} name
   * @param {ChannelContext} contextOrGetter
   */
  channel(name, contextOrGetter) {
    contexts.set(name, contextOrGetter);
    send({ role: 'browser', type: 'register', channel: name });
  },

  /** @param {string} name */
  unregister(name) {
    contexts.delete(name);
    send({ role: 'browser', type: 'unregister', channel: name });
  },

  /** Channel names registered in this tab. */
  channels() {
    return Array.from(contexts.keys());
  },

  helpers,
};

export { lackey };
export default lackey;
